import ChargeHelper from 'features/Charge/chargeHelper';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTPagination, { KTPaginationColor } from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import AppColors from 'general/constants/AppColors';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import { useEffect, useMemo, useState } from 'react';

const defaultForm = {
  name: '',
  description: '',
  displayOrder: 1,
  isVisible: true,
};

function ProductCategoryListScreen() {
  const [filter, setFilter] = useState({ page: 0, limit: 10, q: '', isVisible: '' });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [showFormModal, setShowFormModal] = useState(false);

  const fetchData = () => {
    setLoading(true);
    ChargeHelper.API.requestGetProductCategoryList(filter)
      .then((response) => {
        setItems(response?.categories || []);
        setTotal(response?.total || 0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const categoryOrderOffset = items.some((item) => Number(item?.displayOrder ?? 0) === 0) ? 1 : 0;
  const toVisibleOrder = (value) => Number(value ?? 0) + categoryOrderOffset;

  const openCreate = () => {
    setEditingItem(null);
    setForm({ ...defaultForm, displayOrder: total + 1 });
    setShowFormModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item?.name || '',
      description: item?.description || '',
      displayOrder: toVisibleOrder(item?.displayOrder),
      isVisible: item?.isVisible !== false,
    });
    setShowFormModal(true);
  };

  const closeForm = () => {
    setShowFormModal(false);
    setEditingItem(null);
    setForm(defaultForm);
  };

  const submitForm = async () => {
    if (!form.name?.trim()) {
      ToastHelper.showError('Vui lòng nhập tên danh mục');
      return;
    }

    const payload = {
      ...form,
      name: form.name.trim(),
      displayOrder: Number(form.displayOrder || 1),
    };
    const result = editingItem
      ? await ChargeHelper.API.requestUpdateProductCategory(editingItem.productCategoryId, payload)
      : await ChargeHelper.API.requestCreateProductCategory(payload);

    if (result === true) {
      ToastHelper.showSuccess(editingItem ? 'Cập nhật danh mục thành công' : 'Thêm danh mục thành công');
      closeForm();
      fetchData();
    } else {
      ToastHelper.showError(result || 'Lưu danh mục thất bại');
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Xóa danh mục "${item?.name}"? Nếu đã có sản phẩm chọn danh mục này hệ thống sẽ chặn xóa.`)) return;
    const result = await ChargeHelper.API.requestDeleteProductCategory(item.productCategoryId);
    if (result === true) {
      ToastHelper.showSuccess('Xóa danh mục thành công');
      fetchData();
    } else {
      ToastHelper.showError(result || 'Xóa danh mục thất bại');
    }
  };

  const moveItem = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const nextItems = [...items];
    const [current] = nextItems.splice(index, 1);
    nextItems.splice(nextIndex, 0, current);
    setItems(nextItems.map((item, itemIndex) => ({
      ...item,
      displayOrder: itemIndex + filter.page * filter.limit + 1,
    })));
  };

  const saveOrder = async () => {
    const result = await ChargeHelper.API.requestReorderProductCategories(
      items.map((item, index) => ({
        productCategoryId: item.productCategoryId,
        displayOrder: index + filter.page * filter.limit + 1,
      }))
    );
    if (result === true) {
      ToastHelper.showSuccess('Đã lưu thứ tự danh mục');
      fetchData();
    } else {
      ToastHelper.showError(result || 'Lưu thứ tự thất bại');
    }
  };

  const columns = useMemo(() => [
    {
      name: UIHelper.buildTableColumn('Mã danh mục'),
      selector: (row) => row?.code || `DM${String(row?.productCategoryId || '').padStart(8, '0')}`,
      minWidth: '160px',
      style: tableStyle.columns.link,
    },
    {
      name: UIHelper.buildTableColumn('Tên danh mục'),
      selector: (row) => (
        <span className="cursor-pointer hover-opacity-60" onClick={() => openEdit(row)}>
          {row?.name}
        </span>
      ),
      minWidth: '260px',
      style: tableStyle.columns.link,
    },
    {
      name: UIHelper.buildTableColumn('Mô tả'),
      selector: (row) => (
        <div className="my-2 text-ellipsis-2-lines" style={{ whiteSpace: 'normal' }}>
          {row?.description || '-'}
        </div>
      ),
      minWidth: '360px',
      style: tableStyle.columns.normal,
    },
    {
      name: UIHelper.buildTableColumn('Thứ tự'),
      selector: (row) => Utils.formatNumber(toVisibleOrder(row?.displayOrder)),
      width: '120px',
      right: true,
      style: tableStyle.columns.normal,
    },
    {
      name: UIHelper.buildTableColumn('Hiển thị KH'),
      selector: (row) => (
        <span className={`badge ${row?.isVisible !== false ? 'badge-light-success' : 'badge-light-danger'}`}>
          {row?.isVisible !== false ? 'Đang hiển thị' : 'Đang ẩn'}
        </span>
      ),
      minWidth: '150px',
      style: tableStyle.columns.normal,
    },
    {
      name: UIHelper.buildTableColumn('Thao tác'),
      width: '190px',
      style: {
        position: 'sticky',
        right: 0,
        backgroundColor: '#fff',
        borderLeft: '1px solid #ebedfe',
      },
      cell: (row, index) => (
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-icon btn-sm btn-light" onClick={() => moveItem(index, -1)} title="Đưa lên">
            <i className="fa-solid fa-arrow-up" />
          </button>
          <button className="btn btn-icon btn-sm btn-light" onClick={() => moveItem(index, 1)} title="Đưa xuống">
            <i className="fa-solid fa-arrow-down" />
          </button>
          <button className="btn btn-icon btn-sm btn-light-primary" onClick={() => openEdit(row)} title="Chỉnh sửa">
            <i className="fa-regular fa-pen-to-square" />
          </button>
          <button className="btn btn-icon btn-sm btn-light-danger" onClick={() => deleteItem(row)} title="Xóa">
            <i className="fa-regular fa-trash-can" />
          </button>
        </div>
      ),
    },
  ], [items, filter]);

  const visibleCount = items.filter((item) => item?.isVisible !== false).length;
  const hiddenCount = items.length - visibleCount;

  return (
    <div className="container-fluid px-0">
      {UIHelper.buildCard(
        <div className="pb-4">
          <div className="px-4 pt-4 pb-3 d-flex align-items-start justify-content-between">
            <div>
              <div style={UIHelper.buildFontStyle('700', '22px', AppColors.gray[900])}>
                Danh mục sản phẩm
              </div>
              <div className="mt-2 d-flex align-items-center gap-4" style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[700])}>
                <span>Tổng: <span style={{ color: AppColors.primary }}>{Utils.formatNumber(total)} danh mục</span></span>
                <span>Hiển thị: <span style={{ color: AppColors.success }}>{Utils.formatNumber(visibleCount)}</span></span>
                <span>Đang ẩn: <span style={{ color: AppColors.danger }}>{Utils.formatNumber(hiddenCount)}</span></span>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              {UIHelper.buildGreenButton('Thêm danh mục', openCreate, <i className="fa-regular fa-circle-plus" />)}
              {UIHelper.buildGreenButton('Lưu thứ tự', saveOrder, <i className="fa-solid fa-floppy-disk" />)}
            </div>
          </div>

          <div className="row m-0 p-0 px-2">
            <div className="col-lg-4 col-md-6 mb-4 px-2">
              {UIHelper.buildFormLabel('Tìm kiếm')}
              <KTFormSearch
                value={filter.q}
                placeholder="Theo mã, tên hoặc mô tả danh mục"
                onSubmit={(text) => setFilter({ ...filter, q: text, page: 0 })}
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4 px-2">
              {UIHelper.buildFormLabel('Trạng thái hiển thị')}
              <select
                className="form-control"
                value={filter.isVisible}
                onChange={(event) => setFilter({ ...filter, isVisible: event.target.value, page: 0 })}
              >
                <option value="">Tất cả</option>
                <option value="true">Đang hiển thị</option>
                <option value="false">Đang ẩn</option>
              </select>
            </div>
          </div>

          <div className="px-4">
            <KTTable
              columns={columns}
              items={items}
              selectableRows={false}
              striped={false}
              compactGrid={false}
              loading={loading}
              hasActionColumn
              noDataComponent={UIHelper.buildEmptyNoData('Chưa có danh mục sản phẩm')}
            />
          </div>

          <div className="px-4 mt-2">
            <KTPagination
              currentPage={filter.page + 1}
              rowCount={total}
              rowsPerPage={filter.limit}
              currentItemInPageCount={items.length}
              onChangePage={(page) => setFilter({ ...filter, page: Number(page) - 1 })}
              onChangeRowsPerPage={(limit) => setFilter({ ...filter, limit: Number(limit), page: 0 })}
              loading={false}
              color={KTPaginationColor.success}
              appendText="danh mục"
            />
          </div>
        </div>,
        false,
        false,
        true,
        false,
        AppColors.gray[300]
      )}

      {showFormModal && (
        <div
          className="position-fixed d-flex align-items-center justify-content-center"
          style={{
            inset: 0,
            background: 'rgba(15, 23, 42, 0.35)',
            zIndex: 1050,
            padding: 24,
          }}
          onClick={closeForm}
        >
          <div
            className="bg-white rounded-lg"
            style={{
              width: 'min(560px, 100%)',
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.25)',
              overflow: 'hidden',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-5 py-4 d-flex align-items-center justify-content-between" style={{ borderBottom: `1px solid ${AppColors.gray[200]}` }}>
              <div>
                <div style={UIHelper.buildFontStyle('800', '20px', AppColors.gray[900])}>
                  {editingItem ? 'Sửa danh mục sản phẩm' : 'Thêm danh mục sản phẩm'}
                </div>
                <div className="mt-1" style={UIHelper.buildFontStyle('600', '13px', AppColors.gray[600])}>
                  Danh mục dùng để gom nhóm sản phẩm trên giao diện khách hàng.
                </div>
              </div>
              <button type="button" className="btn btn-icon btn-light" onClick={closeForm}>
                <i className="fa-regular fa-xmark" />
              </button>
            </div>

            <div className="px-5 py-4">
              <div className="mb-4">
                {UIHelper.buildFormLabel('Tên danh mục', true)}
                <input
                  className="form-control"
                  placeholder="VD: Trụ sạc xe máy"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>
              <div className="mb-4">
                {UIHelper.buildFormLabel('Mô tả')}
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Nhập mô tả ngắn cho danh mục"
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                />
              </div>
              <div className="row">
                <div className="col-md-6 mb-4">
                  {UIHelper.buildFormLabel('Thứ tự hiển thị')}
                  <input
                    type="number"
                    className="form-control"
                    value={form.displayOrder}
                    onChange={(event) => setForm({ ...form, displayOrder: event.target.value })}
                  />
                </div>
                <div className="col-md-6 mb-4">
                  {UIHelper.buildFormLabel('Hiển thị khách hàng')}
                  <label className="d-flex align-items-center gap-2 h-40px mb-0" style={{ fontWeight: 700 }}>
                    <input
                      type="checkbox"
                      checked={form.isVisible}
                      onChange={(event) => setForm({ ...form, isVisible: event.target.checked })}
                    />
                    Cho phép hiển thị
                  </label>
                </div>
              </div>
            </div>

            <div className="px-5 py-4 d-flex justify-content-end gap-3" style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}>
              {UIHelper.buildNegativeButton('Hủy bỏ', closeForm)}
              {UIHelper.buildGreenButton(editingItem ? 'Cập nhật' : 'Thêm mới', submitForm)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductCategoryListScreen;
